import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef
} from 'react';
import { useInput, useTranslate } from 'react-admin';
import {
  makeStyles,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  ListItemText,
  Tooltip,
  Switch
} from '@material-ui/core';
import { useFormState, useForm } from 'react-final-form';
import get from 'lodash/get';
import omit from 'lodash/omit';
import defaultTo from 'lodash/defaultTo';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import upperFirst from 'lodash/upperFirst';
import { List, ArrowKeyStepper } from 'react-virtualized';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import SearchIcon from '@material-ui/icons/Search';

import SelectFilterStyles from './styles/SelectFilterStyles';

const useSelectFilterStyles = makeStyles(SelectFilterStyles);

/**
 * Input to select one or multiple values
 * @param {*} props
 */
const SelectFilter = props => {
  const { source, label, variant, multiple, sort, radio } = props;
  const lov = useMemo(() => props.lov || [], [props.lov]);

  const formState = useFormState(props);
  const translate = useTranslate();
  const form = useForm(props);
  const { isRequired } = useInput(omit(props, 'defaultValue'));

  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [scrollToRow, setScrollToRow] = useState(0);
  const [showSelected, setShowSelected] = useState(false);
  const [lastItemSelected, setLastItemSelected] = useState(null);
  const [filteredLov, setFilteredLov] = useState(lov);

  const inputContainerEl = useRef(null);

  /**
   * Update form values to know when user selects all values
   * regardless of the filter filter used
   */
  const setAllValuesChecked = useCallback(
    checked => {
      form.change('allValuesChecked' + upperFirst(source), checked);
    },
    [form, source]
  );

  const classes = useSelectFilterStyles({
    ...props,
    inputContainerEl: inputContainerEl ? inputContainerEl.current : null,
    open
  });

  const value = useMemo(() => {
    if (multiple) {
      return get(formState.values, source, []);
    } else {
      if (get(formState.values, source)) {
        return [get(formState.values, source)];
      }
      return [];
    }
  }, [formState.values, source, multiple]);

  useEffect(() => {
    if (value.length > 0) {
      const lovIds = filteredLov.map(item => item.value);
      const nextCurrentValues = value.filter(id => lovIds.includes(id));
      if (!isEqual(value, nextCurrentValues)) {
        form.change(source, nextCurrentValues);
      }
    }
  }, [filteredLov, value, form, source]);

  useEffect(() => {
    setFilteredLov(lov);
  }, [lov]);

  const sortedChoices = useMemo(() => {
    if (!sort) return filteredLov;
    const collator = new Intl.Collator('en', {
      numeric: false,
      sensitivity: 'base'
    });
    return filteredLov.sort((a, b) =>
      collator.compare(
        a.label === null ? a.label : a.label.toLowerCase(),
        b.label === null ? b.label : b.label.toLowerCase()
      )
    );
  }, [filteredLov, sort]);

  // Creates the choice list with the dataProvider from the filter
  const choices = useMemo(() => {
    let values = sortedChoices.map(item => {
      // Checked if item is in the values
      let isChecked = value.indexOf(item.value) > -1;

      return {
        id: item.value,
        name: item.label,
        isChecked,
        ...item
      };
    });

    if (showSelected) {
      values = values.filter(item => item.isChecked);
    }

    if (searchValue) {
      return values.filter(
        item =>
          (item.name &&
            item.name.toLowerCase().includes(searchValue.toLowerCase())) ||
          item.id.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    return values;
  }, [sortedChoices, value, searchValue, showSelected]);

  /**
   * Overrides onChange's input,
   * To select or unselect all items
   */
  const handleChange = useCallback(
    newValue => {
      let values = newValue;

      // If we can select multiple values
      if (multiple) {
        // If the choice chosen is the "All" item
        if (values.indexOf('*') >= 0) {
          // If all items are selected, unselects all items
          if (value.length === filteredLov.length) {
            form.change(source, []);
            setAllValuesChecked(false);
          } else {
            form.change(
              source,
              filteredLov.map(item => item.value)
            );
            setAllValuesChecked(true);
          }
        } else {
          let currentValues = [...value];
          if (currentValues.indexOf(newValue) >= 0) {
            currentValues.splice(currentValues.indexOf(newValue), 1);
            form.change(source, currentValues);
          } else {
            form.change(source, currentValues.concat([newValue]));
            setLastItemSelected(newValue);
          }
          setSearchValue('');
          setAllValuesChecked(false);
        }
      } else {
        const currentValues = [...value];
        if (!currentValues.includes(newValue)) {
          form.change(source, newValue);
        }
      }
    },
    [form, filteredLov, source, value, multiple, setAllValuesChecked]
  );

  const displayValue = useMemo(() => {
    // If all items are selected, displays "All Items"
    if (
      filteredLov &&
      filteredLov.length > 0 &&
      value.length > 0 &&
      value.length === filteredLov.length
    ) {
      return `All ${label}`;
    }
    // If more 1 items are selected, displays "3 Items selected"
    else if (value.length > 1) {
      return `${value.length} ${label} selected`;
    } else if (!sortedChoices || sortedChoices.length === 0) {
      return null;
    }
    // By default, displays items seperated by a comma
    return sortedChoices
      .filter(choice => value.includes(choice.value))
      .map(choice => choice.label)
      .join(', ');
  }, [value, filteredLov, sortedChoices, label]);

  useEffect(() => {
    if (isRequired) {
      if (touched && value.length === 0) {
        setError('ra.validation.required');
      } else {
        setError(false);
      }
    }
  }, [value, touched, isRequired]);

  useEffect(() => {
    if (lastItemSelected && choices.length === sortedChoices.length) {
      const index = choices.findIndex(choice => choice.id === lastItemSelected);
      setScrollToRow(index);
      setLastItemSelected(null);
    }
  }, [choices, sortedChoices, lastItemSelected]);

  const allItemsChecked = useMemo(() => {
    return value.length === sortedChoices.length;
  }, [value, sortedChoices]);

  // Memoize the component which displays icon for RadioButton or Checkboxe
  const SelectIconComponent = useMemo(() => {
    return multiple ? Checkbox : Radio;
  }, [multiple]);

  // Get component container width
  const inputContainerElHeight = get(inputContainerEl, 'current.offsetWidth');
  // Calculate the width for the suggestions list
  const listWidth = useMemo(() => {
    // - 2 to remove the border
    return defaultTo(inputContainerElHeight, 200) - 2;
  }, [inputContainerElHeight]);

  const uniqId = useCallback(
    selector => {
      return `select-filter-${selector}${source ? '-' + source : ''}`;
    },
    [source]
  );

  const filterUsers = useCallback(
    value => {
      if (value === 'ALL') {
        setFilteredLov(lov);
      } else {
        setFilteredLov(lov.filter(item => item.additionalProperty === value));
      }
    },
    [lov]
  );

  return (
    <FormControl
      variant={variant}
      error={touched && !!error}
      required={isRequired}
      margin="dense"
      classes={{ root: classes.mainContainer }}
    >
      {/** Input label */}
      <InputLabel htmlFor={source} error={touched && !!error}>
        {label}
      </InputLabel>

      {/** Input Container */}
      <div
        ref={inputContainerEl}
        onClick={() => {
          setOpen(true);
        }}
        className={classNames(
          classes.fieldContainer,
          error && classes.fieldContainerError
        )}
        id={uniqId('value')}
      >
        <span className={classes.field}>{displayValue}</span>
        {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
      </div>

      {/** Popover to display suggestions */}
      <div
        className={classNames(
          classes.customPopover,
          open && classes.customPopoverOpen
        )}
        style={{ width: listWidth }}
      >
        {/* List header (only on multiselect) */}
        {sortedChoices.length > 0 && (
          <div className={classes.listHeader}>
            <div className={classes.searchItem}>
              <input
                type="text"
                value={searchValue}
                className={classes.searchInput}
                onKeyDown={evt => {
                  // Click on arrow down to focus the list of item
                  if (evt.keyCode === 40) {
                    document.querySelector('#' + uniqId('list')).focus();
                  }
                }}
                onChange={evt => {
                  setSearchValue(evt.target.value);
                }}
                placeholder="Search ..."
                id={uniqId('search')}
              />
              <SearchIcon className={classes.searchIcon} />
            </div>
            {radio && (
              <RadioGroup
                aria-label="groupsFilter"
                name="groupsFilter"
                onChange={({ target }) => filterUsers(target.value)}
                row
                defaultValue="ALL"
              >
                <FormControlLabel
                  value="ALL"
                  control={<Radio color="primary" />}
                  label="All User Types"
                />
                <FormControlLabel
                  value="Fund Admin"
                  control={<Radio color="primary" />}
                  label="Fund Admins"
                />
                <FormControlLabel
                  value="Regular Contact"
                  control={<Radio color="primary" />}
                  label="Regular Contacts"
                />
              </RadioGroup>
            )}
            {multiple && (
              <MenuItem
                value="selected"
                onClick={() => {
                  setShowSelected(!showSelected);
                }}
                classes={{ root: classes.listHeaderItem }}
                ContainerProps={{
                  id: uniqId('showSelected')
                }}
              >
                <span className={classes.lisItemContainer}>
                  <Switch
                    checked={showSelected}
                    disableRipple={true}
                    id="switch"
                    classes={{ switchBase: classes.switch }}
                  />
                  <ListItemText
                    primary="Show selected"
                    classes={{
                      primary: classes.listItemText,
                      root: classes.listItemTextContainer
                    }}
                  />
                </span>
              </MenuItem>
            )}
            {!showSelected && multiple && isEmpty(searchValue) && (
              <MenuItem
                value="*"
                onClick={() => {
                  handleChange('*');
                }}
                classes={{ root: classes.listHeaderItem }}
                ContainerProps={{
                  id: uniqId('allItems')
                }}
              >
                <span className={classes.lisItemContainer}>
                  <Checkbox
                    id={'All ' + label}
                    checked={allItemsChecked}
                    disableRipple={true}
                    classes={{ root: classes.checkboxAllItemsIcon }}
                  />
                  <ListItemText
                    primary={radio ? 'Select All ' : 'All ' + label}
                    classes={{
                      primary: classes.listItemText,
                      root: classes.listItemTextContainer
                    }}
                  />
                </span>
              </MenuItem>
            )}
          </div>
        )}
        <ArrowKeyStepper
          mode="cells"
          columnCount={1}
          rowCount={choices.length}
          scrollToRow={scrollToRow}
          scrollToColumn={0}
          isControlled
          onScrollToChange={({ scrollToRow: newScrollToRow }) => {
            setScrollToRow(newScrollToRow);
          }}
        >
          {({ onSectionRendered, scrollToRow: scrollToIndex }) => {
            return (
              <List
                width={listWidth}
                id={uniqId('list')}
                height={choices.length * 30 > 300 ? 300 : choices.length * 30}
                rowHeight={30}
                rowCount={choices.length}
                scrollToIndex={scrollToIndex}
                onRowsRendered={onSectionRendered}
                className={classes.list}
                rowRenderer={row => {
                  const { index, key, style } = row;
                  const option = choices[index];
                  return (
                    <Tooltip
                      placement="top"
                      title={option.name || ''}
                      key={key}
                    >
                      <MenuItem
                        onClick={() => {
                          handleChange(option.id);
                        }}
                        style={{
                          ...style,
                          paddingLeft: '0',
                          backgroundColor:
                            scrollToIndex === index ? 'lightgrey' : 'white'
                        }}
                        value={option.id}
                      >
                        <span className={classes.lisItemContainer}>
                          <SelectIconComponent
                            checked={option.isChecked}
                            id={option.name}
                            disableRipple={true}
                          />
                          <ListItemText
                            primary={option.name}
                            classes={{
                              primary: classes.listItemText,
                              root: classes.listItemTextContainer
                            }}
                          />
                        </span>
                      </MenuItem>
                    </Tooltip>
                  );
                }}
              />
            );
          }}
        </ArrowKeyStepper>
      </div>

      {/** Error message */}
      <FormHelperText error={!!error}>
        {!!error ? translate(error) : <span>&#8203;</span>}
      </FormHelperText>

      {/** Backdrop to manage click outside */}
      {open && (
        <div
          className={classes.backdrop}
          onClick={() => {
            setOpen(false);
            setTouched(true);
          }}
          id={uniqId('backdrop')}
        />
      )}
    </FormControl>
  );
};

SelectFilter.propTypes = {
  multiple: PropTypes.bool,
  sort: PropTypes.bool
};

SelectFilter.defaultProps = {
  multiple: true,
  sort: true
};

export default SelectFilter;
